import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { Star, BookOpen, Lock, Heart, PenLine, Eye, Search, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Trophy from "../components/Trophy";

const SORTS = [
  { k: "recent",  tk: "novellas_page.sort_newest" },
  { k: "rating",  tk: "novellas_page.sort_rating" },
  { k: "reads",   tk: "novellas_page.sort_most_read" },
  { k: "popular", tk: "novellas_page.sort_most_liked" },
];

const Pill = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] font-manrope transition-colors whitespace-nowrap ${
      active
        ? "bg-[var(--cream)] text-black"
        : "bg-transparent text-[var(--cream-soft)] border border-white/10 hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
    }`}
  >
    {children}
  </button>
);

const Stars = ({ value, count }) => {
  const full = Math.round(value || 0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= full ? "fill-[var(--magenta)] text-[var(--magenta)]" : "text-white/20"}`}
        />
      ))}
      {count > 0 && (
        <span className="font-meta ml-1 text-[10px]">
          {value.toFixed(1)} · {count}
        </span>
      )}
    </div>
  );
};

export default function Noveller() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [themes, setThemes] = useState([]);
  const [sort, setSort] = useState("recent");
  const [theme, setTheme] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [trophyMap, setTrophyMap] = useState({});

  useEffect(() => {
    api.get("/noveller/themes").then((r) => setThemes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort, limit: "30" });
    if (theme) params.set("theme", theme);
    if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
    api
      .get(`/noveller?${params}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [sort, theme, debouncedQ]);

  // Trofé-batch for synlige noveller
  useEffect(() => {
    if (!items || items.length === 0) { setTrophyMap({}); return; }
    api.post("/contests/content-trophies", { type: "novelle", ids: items.map((n) => n.id) })
      .then((r) => setTrophyMap(r.data?.results || {}))
      .catch(() => setTrophyMap({}));
  }, [items]);

  const isLoggedIn = useMemo(() => user && user !== false, [user]);

  return (
    <Layout withSidebar={false}>
      {/* Kompakt header: tittel venstre, kontroller + knapp høyre */}
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-x-12 gap-y-6 mb-8 items-start">
        {/* Venstre: tittel */}
        <div>
          <div className="font-meta mb-2">{t("novellas_page.label")}</div>
          <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] leading-tight">
            {t("novellas_page.page_title")}.
          </h1>
        </div>

        {/* Høyre: alle filtre + søk + publiser-knapp i kompakt blokk */}
        <div className="lg:pt-3 space-y-3" data-testid="novelle-filter-bar">
          {/* Linje 1: Sortering + søk + publiser-knapp */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="flex items-center gap-1.5">
              <span className="font-meta mr-1">{t("novellas_page.sort")}</span>
              {SORTS.map((s) => (
                <Pill key={s.k} active={sort === s.k} onClick={() => setSort(s.k)} testId={`novelle-sort-${s.k}`}>
                  {t(s.tk)}
                </Pill>
              ))}
            </div>

            <div className="relative flex-1 min-w-[160px] sm:max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--cream-faint)] pointer-events-none" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("novellas_page.search_placeholder")}
                className="w-full bg-transparent border border-white/10 hover:border-white/20 focus:border-[var(--magenta)] focus:outline-none px-7 py-1.5 text-[11px] uppercase tracking-[0.15em] font-manrope text-[var(--cream)] placeholder:text-[var(--cream-faint)]/60 transition-colors"
                data-testid="novelle-search-input"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
                  data-testid="novelle-search-clear"
                  aria-label="Tøm søk"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {isLoggedIn && (
              <Link to="/noveller/ny" className="btn-magenta inline-flex items-center text-xs ml-auto" data-testid="novelle-new-btn">
                <PenLine className="h-3.5 w-3.5 mr-2" /> {t("stories.publish")}
              </Link>
            )}
          </div>

          {/* Linje 2: Tema-filtre */}
          {themes.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-meta mr-1">{t("novellas_page.theme", "Tema")}</span>
              <Pill active={theme === ""} onClick={() => setTheme("")}>{t("common.all", "Alle")}</Pill>
              {themes.slice(0, 18).map((th) => (
                <Pill key={th.theme} active={theme === th.theme} onClick={() => setTheme(th.theme)}>
                  {th.theme}
                </Pill>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
          <div className="font-fraunces italic text-2xl text-[var(--cream-soft)] mb-2">
            {t("stories.no_stories_found")}
          </div>
          {isLoggedIn && (
            <Link to="/noveller/ny" className="font-medium text-sm text-[var(--cream)] border-l-2 border-[var(--magenta)] pl-3 underline">
              {t("stories.be_first_to_publish")}
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="novelle-grid">
          {items.map((n) => (
            <Link
              key={n.id}
              to={`/noveller/${n.id}`}
              data-testid={`novelle-card-${n.id}`}
              className="editorial-card p-6 flex flex-col gap-3 hover:ring-1 hover:ring-[var(--magenta)]/40 transition-all group relative"
            >
              {trophyMap[n.id] && (
                <span className="absolute top-3 right-3 z-10">
                  <Trophy
                    place={trophyMap[n.id].place}
                    contestTitle={trophyMap[n.id].contest_title}
                    contestId={trophyMap[n.id].contest_id}
                    variant="corner"
                    size={16}
                  />
                </span>
              )}
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-fraunces italic text-2xl text-[var(--cream)] leading-tight group-hover:text-[var(--magenta)] transition-colors">
                  {n.title}
                </h3>
                {n.token_cost > 0 ? (
                  <div className="flex items-center gap-1 shrink-0 font-manrope text-xs uppercase tracking-[0.2em] text-[var(--magenta)]">
                    <Lock className="h-3 w-3" /> {n.token_cost}
                  </div>
                ) : (
                  <span className="font-meta text-[10px] text-[var(--cream-soft)]">Gratis</span>
                )}
              </div>

              <p className={`font-fraunces text-[15px] text-[var(--cream-soft)] leading-relaxed line-clamp-4 ${!isLoggedIn ? "blur-md select-none pointer-events-none" : ""}`}>
                {isLoggedIn ? n.teaser : "Logg inn for å lese denne novellen."}
              </p>

              {n.themes?.length > 0 && isLoggedIn && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {n.themes.slice(0, 4).map((t) => (
                    <span key={t} className="font-meta text-[9px] border border-white/10 px-1.5 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/8">
                <div className={`font-meta text-[10px] ${!isLoggedIn ? "blur-sm select-none" : ""}`}>
                  {isLoggedIn ? `${n.author?.username || "ukjent"} · ${n.word_count} ord` : "••••••• · ••• ord"}
                </div>
                <Stars value={isLoggedIn ? n.avg_rating : 0} count={isLoggedIn ? n.rating_count : 0} />
              </div>
              {isLoggedIn && (
                <div className="flex items-center gap-4 font-meta text-[10px]">
                  <span className="flex items-center gap-1">
                    <Eye className="h-3 w-3" /> {n.read_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="h-3 w-3" /> {n.like_count}
                  </span>
                  <span className="ml-auto">{formatRelative(n.created_at)}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
